import React, { useEffect, useState } from "react";
import mcbLogo from "../images/mcb-logo.png";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const SAILINGS_API = `${API_BASE}/sailings`;

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function fmtDateTime(d) {
  if (!d) return "—";
  const date = new Date(d);
  const day = DAY_NAMES[date.getDay()];
  const dateStr = date.toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" });
  const timeStr = date.toLocaleTimeString("en-NZ", { hour: "numeric", minute: "2-digit" });
  return `${day} ${dateStr}, ${timeStr}`;
}

function toTitle(v) {
  return String(v || "").replace(/_/g, " ").replace(/-/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

// Only sailings that are still open for planning/booking appear on the schedule.
const INCLUDED_STATUSES = ["draft", "open"];

export default function SailingSchedulePrint() {
  const [sailings, setSailings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(SAILINGS_API, { cache: "no-store" })
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        const upcoming = list
          .filter((s) => INCLUDED_STATUSES.includes(String(s.status || "draft").toLowerCase()))
          .sort((a, b) => new Date(a.etd) - new Date(b.etd));
        setSailings(upcoming);
      })
      .catch(() => setError("Could not load sailings."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 40, fontFamily: "sans-serif" }}>Loading sailing schedule...</div>;
  if (error) return <div style={{ padding: 40, fontFamily: "sans-serif" }}>{error}</div>;

  const sectionTitleStyle = {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.09em",
    color: "#1a1a1a",
    borderBottom: "1px solid #eee",
    paddingBottom: 6,
    marginBottom: 10,
  };
  const thStyle = {
    padding: "8px 10px",
    textAlign: "left",
    fontWeight: 700,
    fontSize: 9,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    whiteSpace: "nowrap",
  };
  const tdStyle = { padding: "8px 10px" };

  return (
    <div style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", background: "#fff", color: "#1a1a1a", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4 landscape; margin: 14mm 16mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>

      {/* Screen toolbar */}
      <div className="no-print" style={{ background: "#1a1a1a", padding: "12px 32px", display: "flex", alignItems: "center", gap: 16 }}>
        <button onClick={() => window.close()} style={{ background: "none", border: "none", color: "#fff", fontSize: 13, opacity: 0.7, cursor: "pointer" }}>← Close</button>
        <button onClick={() => window.print()} style={{ marginLeft: "auto", background: "#1a1a1a", border: "1px solid #fff", color: "#fff", borderRadius: 6, padding: "8px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          Print / Save PDF
        </button>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 24px 60px" }}>

        {/* ── HEADER ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #1a1a1a", paddingBottom: 16, marginBottom: 20 }}>
          <div>
            <img src={mcbLogo} alt="McCallum Bros Ltd" style={{ height: 44, width: "auto" }} />
            <div style={{ fontSize: 11, color: "#666", marginTop: 6 }}>747 Rosebank Road, Avondale, Auckland</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>SAILING SCHEDULE</div>
            <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
              {sailings.length} upcoming sailing{sailings.length === 1 ? "" : "s"}
            </div>
            <div style={{ fontSize: 11, color: "#666" }}>Generated: {fmtDateTime(new Date())}</div>
          </div>
        </div>

        {/* ── SCHEDULE TABLE ── */}
        <div>
          <div style={sectionTitleStyle}>Upcoming Sailings</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#1a1a1a", color: "#fff" }}>
                <th style={thStyle}>Sailing Ref</th>
                <th style={thStyle}>Vessel</th>
                <th style={thStyle}>Route</th>
                <th style={thStyle}>Departs (ETD)</th>
                <th style={thStyle}>Arrives (ETA)</th>
                <th style={thStyle}>Freight Booking Cutoff</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {sailings.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ ...tdStyle, textAlign: "center", color: "#bbb", fontStyle: "italic", padding: 20 }}>
                    No upcoming sailings scheduled.
                  </td>
                </tr>
              ) : (
                sailings.map((s, i) => (
                  <tr key={s._id || i} style={{ background: i % 2 === 0 ? "#fafafa" : "#fff", borderBottom: "1px solid #eee" }}>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{s.sailingRef}</td>
                    <td style={tdStyle}>{s.vesselName || "—"}</td>
                    <td style={tdStyle}>{s.routeLabel || `${s.origin || "—"} → ${s.destination || "—"}`}</td>
                    <td style={tdStyle}>{fmtDateTime(s.etd)}</td>
                    <td style={tdStyle}>{fmtDateTime(s.eta)}</td>
                    <td style={tdStyle}>{s.bookingCutoff ? fmtDateTime(s.bookingCutoff) : "—"}</td>
                    <td style={tdStyle}>{toTitle(s.status || "draft")}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── NOTICE & FOOTER ── */}
        <div style={{ marginTop: 24, padding: "12px 16px", background: "#f6f6f6", borderLeft: "4px solid #820024", fontSize: 11, lineHeight: 1.5, color: "#333" }}>
          <strong>Indicative schedule only.</strong> All sailing dates and times are subject to change due to weather and operational requirements. Pitt Island calls will be confirmed in line with weather windows.
        </div>
        <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", fontSize: 10, color: "#666" }}>
          <span>McCallum Bros Limited</span>
          <span>Generated {new Date().toLocaleString("en-NZ")}</span>
        </div>

      </div>
    </div>
  );
}